import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalContext';
import { useContent } from '../context/ContentContext';
import { Sun, Moon, Globe, Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { getMenuConfig } from '../lib/menuConfig';

const Header = () => {
  const { t, toggleLanguage, language } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuConfig = getMenuConfig(siteContent?.global?.menu);
  const isHomePage = location.pathname === '/';
  const logoUrl = isDark
    ? (siteContent?.global?.logo_url_dark || "/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png")
    : (siteContent?.global?.logo_url_light || "/uploads/a76604b9-11dd-4737-a8b8-00479d98f42a.png");
  const phoneNumber = t('contact.phone.number');
  const emailAddress = t('contact.email.address');
  const officeCity = t('contact.office.city');
  const phoneHref = `tel:${phoneNumber.replace(/[^+0-9]/g, '')}`;
  const emailHref = `mailto:${emailAddress}`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home', section: 'home' },
    { key: 'about', href: '#about', popup: 'about', section: 'about' },
    { key: 'products', href: '#products', popup: 'products', section: 'products' },
    { key: 'news', href: '/news', popup: null, route: true },
    { key: 'contact', href: '#contact', popup: 'contact', section: 'contact' },
  ].filter(item => menuConfig[item.key]?.enabled !== false);

  const scrollToSection = (selector) => {
    const element = document.querySelector(selector);
    if (!element) return;

    const targetTop = element.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  };

  const navigateToHomepageSection = (section) => {
    navigate({ pathname: '/', hash: `#${section}` });
  };

  const handleSectionClick = (section, popup = null) => {
    if (!isHomePage) {
      navigateToHomepageSection(section);
      setIsMobileMenuOpen(false);
      return;
    }

    if (popup) {
      openModal('section', { section: popup });
    } else {
      scrollToSection(`#${section}`);
    }

    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (item) => {
    if (item.route) {
      navigate(item.href);
    } else if (item.section) {
      handleSectionClick(item.section, item.popup);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header data-testid="header" className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar - Contact Info */}
      <div 
        className={`transition-all duration-300 ${
          isScrolled 
            ? 'h-0 overflow-hidden opacity-0' 
            : 'h-auto opacity-100'
        } ${
          isDark
            ? 'bg-[#0A0A0A] border-b border-[var(--border-color)]'
            : 'bg-[#F5F0E8] border-b border-[rgba(184,134,11,0.2)]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between py-2 text-xs sm:text-sm ${isRtl ? 'flex-row-reverse' : ''}`}>
            {/* Contact Info - Left */}
            <div className={`flex items-center gap-4 sm:gap-6 ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              <a 
                href={phoneHref} 
                className={`flex items-center gap-2 hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                data-testid="header-phone"
              >
                <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline" dir="ltr">{phoneNumber}</span>
              </a>
              <a 
                href={emailHref} 
                className={`flex items-center gap-2 hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                data-testid="header-email"
              >
                <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline" dir="ltr">{emailAddress}</span>
              </a>
              <div className={`hidden md:flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{officeCity}</span>
              </div>
            </div>

            {/* Social & Actions - Right */}
            <div className={`flex items-center gap-3 sm:gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
              {/* Social Icons */}
              <div className={`hidden sm:flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <a href="#" className={`${isDark ? 'text-white/50' : 'text-gray-400'} hover:text-[var(--gold-primary)] transition-colors`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
                </a>
                <a href="#" className={`${isDark ? 'text-white/50' : 'text-gray-400'} hover:text-[var(--gold-primary)] transition-colors`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" className={`${isDark ? 'text-white/50' : 'text-gray-400'} hover:text-[var(--gold-primary)] transition-colors`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              </div>

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/70' : 'text-gray-600'} hover:text-[var(--gold-primary)] transition-colors`}
                data-testid="language-toggle-btn"
                aria-label="Toggle Language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-medium">{language === 'en' ? 'AR' : 'EN'}</span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-1.5 ${isDark ? 'text-white/70' : 'text-gray-600'} hover:text-[var(--gold-primary)] transition-colors`}
                data-testid="theme-toggle-btn"
                aria-label="Toggle Theme"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div 
        className={`transition-all duration-300 ${
          isScrolled 
            ? isDark 
              ? 'bg-[#050505]/95 backdrop-blur-xl shadow-lg shadow-black/20' 
              : 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/10'
            : isDark 
              ? 'bg-[#111111]/80 backdrop-blur-md' 
              : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between py-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {/* Logo */}
            <a
              href={isHomePage ? '#home' : '/#home'}
              onClick={(e) => {
                e.preventDefault();
                handleSectionClick('home');
              }}
              className={`flex items-center gap-3 group ${isRtl ? 'flex-row-reverse' : ''}`}
              data-testid="logo-link"
            >
              <img
                src={logoUrl}
                alt="Royal Tobacco"
                className="h-14 sm:h-16 lg:h-[4.5rem] w-auto transition-transform group-hover:scale-105"
              />
              <div className={`hidden sm:block ${isRtl ? 'text-right' : 'text-left'}`}>
                <span className="font-display text-xl sm:text-2xl font-bold gold-text block leading-tight">
                  ROYAL
                </span>
                <span className={`text-[10px] sm:text-xs tracking-[0.3em] uppercase ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  TOBACCO
                </span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className={`hidden lg:flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`} data-testid="desktop-nav">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item);
                  }}
                  className={`relative px-5 py-2 font-body text-sm font-medium transition-colors duration-200 uppercase tracking-wider group ${
                    isDark ? 'text-white/80 hover:text-[var(--gold-primary)]' : 'text-gray-700 hover:text-[var(--gold-primary)]'
                  }`}
                  data-testid={`nav-${item.key}`}
                >
                  {t(`nav.${item.key}`)}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[var(--gold-primary)] group-hover:w-3/4 transition-all duration-300" />
                </a>
              ))}

              {/* CTA Button */}
              {menuConfig.contact?.enabled !== false && (
                <button
                  onClick={() => handleSectionClick('contact', 'contact')}
                  className={`${isRtl ? 'mr-4' : 'ml-4'} px-6 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all duration-300 btn-shine`}
                  data-testid="nav-contact-cta"
                >
                  {t('nav.contact')}
                </button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${isDark ? 'text-white' : 'text-gray-800'}`}
              data-testid="mobile-menu-btn"
              aria-label="Toggle Mobile Menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } ${isDark ? 'bg-[#111111]' : 'bg-white'}`}
          data-testid="mobile-nav"
        >
          <nav className={`px-4 pb-6 pt-2 ${isRtl ? 'text-right' : 'text-left'}`}>
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item);
                  }}
                  className={`px-4 py-3 font-body text-base font-medium transition-colors duration-200 rounded-sm ${
                    isDark
                      ? 'text-white/80 hover:text-[var(--gold-primary)] hover:bg-white/5'
                      : 'text-gray-700 hover:text-[var(--gold-primary)] hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t(`nav.${item.key}`)}
                </a>
              ))}
            </div>
            
            {/* Mobile Contact Info */}
            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <a 
                href={phoneHref} 
                className={`flex items-center gap-3 px-4 py-2 text-sm ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/60' : 'text-gray-500'}`}
              >
                <Phone className="w-4 h-4 text-[var(--gold-primary)]" />
                <span dir="ltr">{phoneNumber}</span>
              </a>
              <a 
                href={emailHref} 
                className={`flex items-center gap-3 px-4 py-2 text-sm ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/60' : 'text-gray-500'}`}
              >
                <Mail className="w-4 h-4 text-[var(--gold-primary)]" />
                <span dir="ltr">{emailAddress}</span>
              </a>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
